import { useLayoutEffect, type RefObject } from 'react'
import { useViewContext } from '../view-context'
import { useGetViewScrollPosition, useSetViewScrollPosition } from '../view-atoms'

interface ViewEntryScrollPersistenceOptions {
  /**
   * False for a body that owns where it opens — a live transcript always opens
   * at its bottom edge, so a remembered offset is something to discard, not to
   * restore (HIG-5978). Nothing is saved either, so no offset survives to be
   * restored on the next open.
   */
  enabled?: boolean
}

/**
 * Per-history-entry scroll persistence (ADR 0001): saves the view body's
 * scroll offset under its history entry key and restores it when the entry
 * remounts on back/forward. Fresh entries start at the top.
 */
export function useViewEntryScrollPersistence(
  scrollRef: RefObject<HTMLElement | null | undefined>,
  options: ViewEntryScrollPersistenceOptions = {},
) {
  const { enabled = true } = options
  const { entryKey } = useViewContext()
  const getScrollPosition = useGetViewScrollPosition()
  const setScrollPosition = useSetViewScrollPosition()

  useLayoutEffect(() => {
    const el = scrollRef.current
    if (!el) {
      return
    }

    if (!enabled) {
      // Drop whatever this entry had stored, rather than merely leaving it
      // alone. `enabled` is usually derived from state that hydrates *after*
      // mount — a Meeting View reads `live` from GlobalState, which starts at
      // its default on a remount — so the run before this one was an enabled
      // one that both restored an offset and, in its cleanup, saved the
      // current one right back. Clearing here is what makes the next open
      // start from the top of that decision instead of inheriting it.
      setScrollPosition(entryKey, 0)
      return
    }

    let raf = 0
    const saved = getScrollPosition(entryKey)
    if (saved > 0) {
      el.scrollTop = saved
      // Async content often lays out after mount; retry once next frame so the
      // offset isn't clamped by a not-yet-measured list. Only when the write was
      // actually clamped, and only while nothing else has moved the scroller
      // since — an unconditional re-assert overwrites whatever landed in that
      // frame, a scroll-to-bottom on open included.
      const clamped = el.scrollTop
      if (clamped !== saved) {
        raf = requestAnimationFrame(() => {
          if (el.scrollTop === clamped) el.scrollTop = saved
        })
      }
    }

    const handleScroll = () => {
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(() => setScrollPosition(entryKey, el.scrollTop))
    }
    el.addEventListener('scroll', handleScroll, { passive: true })
    return () => {
      el.removeEventListener('scroll', handleScroll)
      cancelAnimationFrame(raf)
      setScrollPosition(entryKey, el.scrollTop)
    }
  }, [entryKey, enabled, scrollRef, getScrollPosition, setScrollPosition])
}
